<template>
    <section class="select-a-dealer" id="selectADealer2">
        <div class="select-a-dealer__container container">
            <StepBar />

            <StepLink :step="shortOnlineServiceBooking ? 5 : 4" />

            <template v-if="submitting">
                <LoadingSpinner />
            </template>
            <template v-else>
                <h3 class="title select-date-time__title">Select a date & time</h3>
                <p v-if="bookingAvailabilityMessage" role="alert" class="select-date-time__validation-error">{{ bookingAvailabilityMessage }}</p>
                <p class="select-date-time__heading-text">Now you’ve selected {{ selectedDealer.name }}, please select date and time you’d like to drop your vehicle off.</p>

                <div v-if="noAvailableDates" class="select-date-time__notice">
                    <p>There are currently no dates available to book online at {{ selectedDealer.name }}.</p>
                    <p v-if="selectedDealer.full_slug">Please <a class="link" :href="'/' + selectedDealer.full_slug" target="_blank">contact your dealer here</a> to arrange your booking.</p>
                </div>
                <div v-else-if="showDiagnosticLeadTimeNotice" class="select-date-time__notice">
                    <p>As you’ve selected a diagnostic appointment, the next available booking is currently on {{ formattedMinDate }}.</p>
                    <p>If you need to speak to your dealer sooner, you can <a class="link" :href="'/' + selectedDealer.full_slug" target="_blank">contact them here</a>.</p>
                </div>
                <div class="select-date-time__container">
                    <div class="select-date-time__dates-and-dealer">
                        <div class="card select-a-dealer__drop-off-date-card">
                            <div class="card__inner select-a-dealer__card-flex">
                                <div class="date-time__container">
                                    <div class="date-time__top">

                                        <p v-if="loadingAvailability || loadingTimes" role="status">Checking availability…</p>
                                        <button v-if="availabilityError" type="button" class="btn btn--primary" @click="refreshAvailability">Retry loading dates</button>
                                        <div class="flatpickr" v-show="!noAvailableDates && !loadingAvailability && !availabilityError">
                                            <input
                                                type="text"
                                                name="date"
                                                ref="date"
                                                id="date"
                                                class="form-control form-control--select select-a-dealer__select-input select-date-time__select-input"
                                                :value="formattedDate"
                                                placeholder="Please select a date"
                                            />
                                            <button class="input-button" @click="toggleDateSelect"></button>
                                        </div>

                                        <div class="select-a-dealer__desktop-dealer-info">
                                            <h3 class="card__heading">Dealer Address</h3>
                                            <p class="card__text">{{ selectedDealer.name }} <br /></p>
                                            <p class="card__text">
                                                {{ selectedDealer.address }} <br />
                                                {{ selectedDealer.postcode }} <br />
                                            </p>
                                            <br />
                                            <!--  <p class="card__text"><strong>Hours:</strong> close soon 6pm, opens 9AM Wed</p>-->
                                            <br />
                                            <!--
                                    <p class="card__text">
                                        Contact us on: <br />
                                        <span class="select-a-dealer__card-phone-number">{{ selectedDealer.tel_general }}</span> <br />
                                    </p>
                                    -->
                                        </div>
                                    </div>

                                    <div v-if="timesForSelectedDate && timesForSelectedDate !== 'closed'" class="date-time__bottom">
                                        <p class="date-time__times-title">Please select a timeslot:</p>
                                        <div class="date-time__times-container">
                                            <div v-for="(time, index) in timesForSelectedDate" :key="index" class="date-time__times-tile" :class="{ selected: selectedTime === time }" @click="setTime(time)">
                                                <input type="radio" :id="time.split(' ').join('')" name="bookingTime" :value="time.split(' ').join('')" class="select-a-dealer__radio-input" />
                                                <label :for="time.split(' ').join('')" class="find-my-car__time-label"> {{ time }} </label>
                                            </div>
                                        </div>
                                    </div>

                                    <div v-if="timesForSelectedDate == 'closed'" class="select-date-time__dealer-closed">This dealership is closed on this day, please select another day.</div>
                                    <button type="button" class="btn btn--primary btn--full-width card__button select-date-time__mobile-button" :disabled="reserving || checkingBooking || submitting || loadingAvailability || loadingTimes || availabilityError" @click.prevent="next_step">
                                        <span class="find-my-car__used-btn-txt">Continue</span>
                                    </button>
                                </div>
                            </div>
                        </div>

                        <p v-if="validationMessage" class="select-date-time__validation-error">{{ validationMessage }}</p>

                        <button type="button" class="btn btn--primary btn--full-width card__button select-date-time__desktop-button" :disabled="reserving || checkingBooking || submitting || loadingAvailability || loadingTimes || availabilityError" @click.prevent="next_step">
                            <span class="find-my-car__used-btn-txt">{{ shortOnlineServiceBooking ? 'Confirm Booking' : 'Continue' }}</span>
                        </button>
                    </div>

                    <BookingOverview />
                </div>
            </template>
        </div>
    </section>
</template>

<script>
import { mapActions, mapState, mapMutations } from "vuex/dist/vuex.esm.browser";
import { BookingOverview, StepBar, StepLink } from "../";
import EventObject from "../../../modules/tracking/EventObject";
import LoadingSpinner from "../../components/LoadingSpinner.vue";
import { format, parse, setHours } from "date-fns";
import { mapGetters } from "vuex";

export default {
    components: {
        BookingOverview,
        LoadingSpinner,
        StepBar,
        StepLink,
    },
    props: {
        submitting: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            minDate: null,
            maxDate: null,
            validationMessage: false,
            flatpickrInstance: false,
            timesForSelectedDate: null,
            noAvailableDates: false,
            availableDates: [],
            failedDates: [],
            loadingAvailability: false,
            loadingTimes: false,
            availabilityError: false,
            availabilityRequest: null,
            timesRequestId: 0,
            requestedDate: null,
            destroyed: false,
            reserving: false,
            checkingBooking: false,
        };
    },

    computed: {
        ...mapGetters("OSB", ['hasManufacturerUpdateSelected', 'hasSystemDiagnosticSelected', 'isRecallOnlyBooking', 'latestSelectableTime']),
        ...mapState("Dealers", ["selectedDealer"]),
        ...mapState("OSB", ["selectedDate", "selectedTime", "selectedVehicle", "otherSelected", "nonFranchiseVehicle", "shortOnlineServiceBooking", "manufacturerUpdatesSelected", "bookingId", "bookingAvailabilityMessage"]),

        selectableDates() {
            return this.isRecallOnlyBooking ? this.availableDates.slice(0, 2) : this.availableDates;
        },

        formattedDate() {
            return this.selectedDate ? new Date(this.selectedDate).toDateString() : "";
        },

        showDiagnosticLeadTimeNotice() {
            return this.hasSystemDiagnosticSelected && !!this.minDate && !!this.selectedDealer.full_slug;
        },

        formattedMinDate() {
            return this.minDate ? format(this.minDate, 'dddd D MMMM') : "";
        },

        directionsLink() {
            return this.selectedDealer.address ? `https://www.google.com/maps?daddr=${this.selectedDealer.address}+${this.selectedDealer.town}+${this.selectedDealer.postcode}` : "#";
        },

        /*
        * Function to provide times at 15 minute intervals rather than 30 min provided by
        * dealer-opening-times api
        * */
        // hardcodedDealerTimes() {
        //     return hardCodedTimes();
        // },
    },

    watch: {
        selectedTime(val) {
            if (val && this.selectedDate) {
                this.validationMessage = false;
            }
        },
    },

    methods: {
        ...mapMutations("OSB", ["incrementStep", "setSelectedDate", "setSelectedTime", "setBookingAvailabilityMessage", "markDealerUnavailable", "setStep"]),
        ...mapActions("OSB", ["throwFatalError"]),

        availabilityParams() {
            return {
                location_id: this.selectedDealer.id,
                booking_id: this.bookingId,
                has_takata_recall: String(this.isRecallOnlyBooking),
                has_recall: String(this.hasManufacturerUpdateSelected),
                has_diagnostic: String(this.hasSystemDiagnosticSelected),
                latest_selectable_time: this.latestSelectableTime,
            };
        },

        clearSelectedDateAndTime() {
            this.setSelectedDate(null);
            this.setSelectedTime(null);
            this.timesForSelectedDate = null;
            if (this.flatpickrInstance) {
                this.flatpickrInstance.clear(false);
            }
        },

        async refreshAvailability() {
            if (this.availabilityRequest) {
                return this.availabilityRequest;
            }
            this.loadingAvailability = true;
            this.availabilityError = false;
            this.availabilityRequest = (async () => {
                try {
                    const response = await axios.get('/api/osb/available-dates', { params: this.availabilityParams() });
                    if (this.destroyed) {
                        return;
                    }
                    this.availableDates = response.data.filter(date => !this.failedDates.includes(date));
                    if (this.availableDates.length === 0) {
                        await this.handleNoAvailability();
                        return;
                    }
                    this.updateDatePicker();
                } catch (error) {
                    if (!this.destroyed) {
                        this.availabilityError = true;
                        this.validationMessage = 'Unable to load available dates. Please try again.';
                    }
                } finally {
                    this.loadingAvailability = false;
                    this.availabilityRequest = null;
                }
            })();
            return this.availabilityRequest;
        },

        async handleNoAvailability() {
            this.clearSelectedDateAndTime();
            const response = await axios.post('/api/osb/availability-issue', this.availabilityParams());
            if (this.destroyed) {
                return;
            }
            this.availableDates = response.data.dates.filter(date => !this.failedDates.includes(date));
            if (this.availableDates.length > 0) {
                this.updateDatePicker();
                return;
            }
            this.noAvailableDates = true;
            this.markDealerUnavailable(this.selectedDealer.id);
            this.setBookingAvailabilityMessage(`${this.selectedDealer.name} has no available booking dates. Please select another dealer.`);
            this.$store.commit('Dealers/setSelectedDealer', {});
            this.setStep(3);
            this.scrollToTop();
        },

        updateDatePicker() {
            if (this.selectedDate && !this.selectableDates.includes(format(this.selectedDate, 'YYYY-MM-DD'))) {
                this.clearSelectedDateAndTime();
            }
            this.minDate = parse(this.selectableDates[0]);
            this.maxDate = parse(this.selectableDates[this.selectableDates.length - 1]);
            this.initializeDatePicker();
            this.flatpickrInstance.set({
                enable: this.selectableDates.map(date => parse(date)),
                minDate: this.minDate,
                maxDate: this.maxDate,
            });
            this.flatpickrInstance.jumpToDate(this.selectedDate || this.minDate);
        },

        initializeDatePicker() {
            if (this.flatpickrInstance || this.selectableDates.length === 0) {
                return;
            }
            this.flatpickrInstance = flatpickr(this.$refs.date, {
                minDate: this.minDate,
                maxDate: this.maxDate,
                enable: this.selectableDates.map(date => parse(date)),
                altInput: false,
                dateFormat: 'D J F Y',
                locale: { firstDayOfWeek: 1 },
                onChange: ([selectedDate]) => {
                    if (!selectedDate) {
                        this.timesRequestId++;
                        this.loadingTimes = false;
                    }
                    this.setSelectedTime(null);
                    this.timesForSelectedDate = null;
                    this.setSelectedDate(selectedDate ? setHours(selectedDate, 12) : null);
                    if (selectedDate) {
                        this.getDealerOpeningTimes();
                    }
                },
            });
        },

        toggleDateSelect() {
            if (this.flatpickrInstance && !this.loadingAvailability) {
                this.flatpickrInstance.open();
            }
        },

        async getDealerOpeningTimes() {
            if (!this.selectedDate) {
                return;
            }
            const date = format(this.selectedDate, 'YYYY-MM-DD');
            if (this.loadingTimes && this.requestedDate === date) {
                return;
            }
            this.requestedDate = date;
            const requestId = ++this.timesRequestId;
            this.loadingTimes = true;
            this.timesForSelectedDate = null;
            this.setSelectedTime(null);
            try {
                const response = await axios.get('/api/dealer-opening-times', {
                    params: {
                        dealer: this.selectedDealer.id,
                        service_type: 'Service',
                        interval: '15 minutes',
                        date,
                        osb_booking: true,
                        booking_id: this.bookingId,
                        has_takata_recall: this.isRecallOnlyBooking,
                        has_recall: this.hasManufacturerUpdateSelected,
                        has_diagnostic: this.hasSystemDiagnosticSelected,
                        latest_selectable_time: this.latestSelectableTime,
                    },
                });
                if (this.destroyed || requestId !== this.timesRequestId) {
                    return;
                }
                const times = Array.isArray(response.data)
                    ? response.data.filter(time => typeof time === 'string' && /^\d{1,2}:\d{2}\s*[AP]M$/i.test(time.trim()))
                    : [];
                if (times.length === 0) {
                    this.failedDates.push(date);
                    this.clearSelectedDateAndTime();
                    this.availableDates = this.availableDates.filter(availableDate => availableDate !== date);
                    if (this.flatpickrInstance) {
                        this.flatpickrInstance.set('enable', this.selectableDates.map(date => parse(date)));
                    }
                    this.validationMessage = 'This date has no available times. Please select another date.';
                    await this.refreshAvailability();
                    if (!this.destroyed && this.flatpickrInstance && this.availableDates.length) {
                        this.flatpickrInstance.open();
                    }
                    return;
                }
                this.timesForSelectedDate = times;
            } catch (error) {
                if (!this.destroyed && requestId === this.timesRequestId) {
                    this.validationMessage = 'Unable to load times. Please select the date again to retry.';
                    this.clearSelectedDateAndTime();
                }
            } finally {
                if (requestId === this.timesRequestId) {
                    this.loadingTimes = false;
                }
            }
        },

        async setTime(time) {
            // Reserve for every journey; the API applies lead time and capacity consistently.
            if (this.reserving || !this.selectedDate || this.selectedTime === time) {
                return;
            }
            const selectedDate = format(this.selectedDate, 'YYYY-MM-DD');
            this.reserving = true;
            this.setSelectedTime(null);
            try {
                await axios.post('/api/osb/reservations', {
                    ...this.availabilityParams(),
                    booking_id: this.bookingId,
                    location_id: this.selectedDealer.id,
                    booking_date: selectedDate + ' ' + time,
                });
                if (!this.destroyed && this.selectedDate && format(this.selectedDate, 'YYYY-MM-DD') === selectedDate) {
                    this.setSelectedTime(time);
                    this.setBookingAvailabilityMessage('');
                }
            } catch (error) {
                if (this.destroyed || !this.selectedDate || format(this.selectedDate, 'YYYY-MM-DD') !== selectedDate) {
                    return;
                }
                this.validationMessage = error.response && error.response.status === 409
                    ? error.response.data.message : 'Unable to reserve this time. Please try again.';
                if (error.response && error.response.status === 409) {
                    this.clearSelectedDateAndTime();
                    await this.refreshAvailability();
                }
            } finally {
                this.reserving = false;
            }
        },

        checkForExistingBooking() {
            return axios({
                method: "POST",
                url: "/api/osb/existing-booking",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
                data: {
                    registration: this.selectedVehicle.registration,
                    date: format(this.selectedDate, "YYYY-MM-DD HH:mm:ss"),
                    time: this.selectedTime,
                    locationId: this.selectedDealer.id,
                },
            })
                .then(response => {
                    if (response.data.record_found !== false && response.data.record_found >= 0) {
                        this.validationMessage = "A booking already exists for this vehicle with the selected dealer made within last 30 days.";
                        return;
                    }

                    if (! this.shortOnlineServiceBooking) {
                        this.incrementStep();
                    }
                    this.scrollToTop();
                    return true;
                })
                .catch(error => {
                    this.validationMessage = 'Unable to check your booking. Please try again.';
                    console.error(error);
                    // this.setError(true);
                    return false;
                });
        },

        scrollToTop() {
            window.scrollTo(0, 0);
        },

        async next_step() {
            if (this.checkingBooking || this.submitting) {
                return;
            }
            if (this.reserving || this.loadingAvailability || this.loadingTimes || this.availabilityError || !this.selectedDate || !this.selectedTime) {
                this.validationMessage = "Please select a date & time";
                return;
            }
            new EventObject("OSB", "Click", "Select date and time continue clicked").dispatch();
            this.checkingBooking = true;
            try {
                if (!await this.checkForExistingBooking()) {
                    return;
                }

                this.$emit('collect-manufacturer-update');

                if (this.shortOnlineServiceBooking) {
                    this.$emit('create-booking');
                }
            } finally {
                this.checkingBooking = false;
            }
        },

    },

    async mounted() {
        await this.refreshAvailability();
        if (!this.destroyed && this.selectedDate && !this.availabilityError) {
            await this.getDealerOpeningTimes();
        }
    },

    beforeDestroy() {
        this.destroyed = true;
        this.timesRequestId++;
        if (this.flatpickrInstance) {
            this.flatpickrInstance.destroy();
        }
    },
};
</script>

<style lang="scss" scoped>
.selected {
    border-color: #0293d6;
    color: #0293d6;
}
</style>